<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="flex shrink-0 items-center justify-center">
      <wui-input
        v-model="search"
        class="w-full mt-2"
        :placeholder="$t('common.contactsAndChatsSearch')"
        with-clear
        :prepend-icon="isLoading ? 'spinner' : 'searchM'"
      />
    </div>
    <div class="relative min-h-0 flex-1 px-3 py-2">
      <div
        v-if="processedListItems.length"
        class="scrollable-list"
      >
        <virtual-scroll
          class="h-full overflow-y-auto scroller"
          field="id"
          :items="processedListItems"
          :item-height="64"
          :padding="0"
          :loading="isContactsLoading!"
          @end-reached="fetchNext"
        >
          <template #item="{ item }">
            <chats-list-item
              class="w-full"
              :room="item"
              @click="addContact(item.pServedUser)"
            />
          </template>
        </virtual-scroll>
      </div>
      <div
        v-else
        class="flex flex-col items-center p-10"
      >
        <wui-icon
          name="userM"
          class="h-[64px]! w-[64px]! text-primary-tints-250"
        />
        <p class="text-1324 text-primary">
          {{ $t("chat.noContactsToAdd") }}
        </p>
      </div>
    </div>
    <section
      v-if="selected.length"
      class="mb-4 flex shrink-0 flex-col gap-y-2 px-4"
    >
      <h4 class="text-1316 font-light text-primary-tints-800">
        {{ $t('chat.selectedUsersAndChats') }}
      </h4>
      <hr class="border-t border-bor-nav">
      <chat-forward-message-selected-contacts
        :items="selectedRoomsItems"
        @remove="removeContact($event)"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { WuiIcon, WuiInput, WuiVirtualScroll } from '@wui/common-library'
import { PServed } from '@wui/im'
import throttle from 'lodash.throttle'
import { storeToRefs } from 'pinia'
import { Component, computed, ref, toRef, watch } from 'vue'


import { ChatForwardMessageSelectedContacts } from '@/features/chat-forward-message-selected-contacts'

import { ChatsListItem } from '@/entities/chat'
import { useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'

import { useSearch } from '@/shared/composables/search-handlers'
import { RoomListItem } from '@/shared/types/app'
import { matchesSystemBotPServedQuery } from '@/shared/utils/contacts'
import { createPrivateRoom } from '@/shared/utils/im'

const props = defineProps<{
  sourcePServed: string
  loading?: boolean
  modelValue: PServed[]
}>()

const emit = defineEmits<{
  (event: 'submit'): void
  (event: 'update:modelValue', value: Array<PServed>): void
}>()

const storeRooms = useStoreRooms()
const { roomsWithOtherContactsListItems } = storeToRefs(storeRooms)

const VirtualScroll = WuiVirtualScroll as Component

const selected = toRef(props.modelValue)
const selectedRoomsItems = computed<Array<RoomListItem>>(
  () => selected.value
    .map((pServed) => roomsWithOtherContactsListItems.value.find(r => r.pServedUser === pServed))
    .filter((r) => !!r),
)

const processedListItems = computed(() => {
  return filtered.value
    .reduce((acc: Array<RoomListItem>, item) => {

      if (selected.value.indexOf(item.pServedUser) >= 0) {
        return acc
      }

      acc.push((item as RoomListItem))

      return acc
    }, [])
})

const { search, filtered } = useSearch(
  roomsWithOtherContactsListItems,
  (el: RoomListItem, search: string): boolean => {
    return matchesSystemBotPServedQuery(search, el.pServedUser) || el.name.toLowerCase().includes(search.toLowerCase())
  })

const { fetchContactsByNameOrNumberIfListIsNotLoaded, fetchNext, isLoading: isContactsLoading } = useStoreContacts()
const isLoading = ref(false)
watch(search, throttle(async (v: string) => {
  if (v && v.length > 2) {
    try {
      isLoading.value = true
      const contacts = await fetchContactsByNameOrNumberIfListIsNotLoaded(v)
      contacts.forEach((c) => createPrivateRoom(c.imLogin, c))
    } catch (e) {
      console.error(e)
    } finally {
      isLoading.value = false
    }
  }
}, 500))

const removeContact = (pServed: PServed) => {
  const index = selected.value.findIndex((item) => item === pServed)
  if (index !== -1) {
    selected.value.splice(index, 1)
    emit('update:modelValue', selected.value)
  }
}

const addContact = (pServed: PServed) => {
  selected.value.push(pServed)
  emit('update:modelValue', selected.value)
}
</script>
